/**
 * NavigationEventService - Provides navigation capabilities for components outside Router context
 * 
 * This service allows modals and other components that are rendered outside of Router context
 * to trigger navigation actions. For web, this uses React Router's history API.
 */

export interface NavigationEvent {
  type: 'navigate' | 'goBack' | 'replace';
  path?: string;
  timestamp: number;
}

type NavigationEventListener = (event: NavigationEvent) => void;

class NavigationEventServiceClass {
  private static instance: NavigationEventServiceClass;
  private listeners = new Set<NavigationEventListener>();
  private navigationFunction: ((path: string) => void) | null = null;

  private constructor() {}

  public static getInstance(): NavigationEventServiceClass {
    if (!NavigationEventServiceClass.instance) {
      NavigationEventServiceClass.instance = new NavigationEventServiceClass();
    }
    return NavigationEventServiceClass.instance;
  }

  /**
   * Set the navigation function (called by Navigation component)
   */
  public setNavigationFunction(navFn: (path: string) => void): void {
    this.navigationFunction = navFn;
  }

  /**
   * Navigate to a path
   */
  public navigateTo(path: string): void {
    if (this.navigationFunction) {
      this.navigationFunction(path);
    } else {
      // Fallback to window.location for when no navigation function is set
      if (typeof window !== 'undefined') {
        window.location.href = path;
      }
    }
    
    this.emitEvent({
      type: 'navigate',
      path,
      timestamp: Date.now()
    });
  }

  /**
   * Go back - uses browser history
   */
  public goBack(): void {
    if (typeof window !== 'undefined' && window.history) {
      window.history.back();
    }
    
    this.emitEvent({
      type: 'goBack',
      timestamp: Date.now()
    });
  }

  /**
   * Replace current route
   */
  public replaceTo(path: string): void {
    if (typeof window !== 'undefined' && window.history) {
      window.history.replaceState(null, '', path);
      // Trigger navigation after replace
      this.navigateTo(path);
    }
    
    this.emitEvent({
      type: 'replace',
      path,
      timestamp: Date.now()
    });
  }

  /**
   * Subscribe to navigation events
   */
  public subscribe(listener: NavigationEventListener): () => void {
    this.listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emitEvent(event: NavigationEvent): void {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        // // Error: Navigation event listener failed
      }
    });
  }
}

export const NavigationEventService = NavigationEventServiceClass.getInstance();